import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Tooltip,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { LogoutButton } from "src/components/LogoutButton";
import { CustomButton } from "src/components/CustomButton";
import { useAuthContext } from "src/context/AuthContext";
import { useSubscriptionContext } from "src/context/SubscriptionContext";
import { useIsAdminQuery } from "src/hooks/user/useIsAdminQuery";
import { SubscriptionType } from "src/api/services/user/userSubscriptionService";
import { blueGradient, customColors } from "src/theme";
import FlagSelector from "src/components/Flag/Flag";
import { trackNavigationClickEvent } from "src/utils/tracking";

type NavItem = { text: string; link: string };
const WHITE_NAVBAR_PATHS = [
  "/upload",
  "/myphotos",
  "/settings",
  "/payment-status",
  "/photo",
  "/check-your-email",
];

export const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout, isUserVerified } = useAuthContext();
  const { subscriptionResult } = useSubscriptionContext();
  const { isAdmin } = useIsAdminQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMobile = useMediaQuery("(max-width: 768px)"); // Detect mobile screens

  const menuItems = useMemo(() => {
    const pricingItem = { text: t("NAV.PRICING"), link: "/pricing" };
    const galleryItem = { text: t("NAV.GALLERY"), link: "/gallery" };
    const myPhotosItem = { text: t("NAV.MY_PHOTOS"), link: "/myphotos" };
    const settingsItem = { text: t("NAV.SETTINGS"), link: "/settings" };
    const adminItem = { text: t("NAV.ADMIN_CONSOLE"), link: "/admin" };

    const items: NavItem[] = [{ text: t("NAV.HOME"), link: "/" }];

    // Add items based on user verification and subscription plan
    if (!isUserVerified) {
      items.push(galleryItem, pricingItem); // For non-verified users, show Gallery and Pricing
    } else {
      if (subscriptionResult?.plan === SubscriptionType.FREE) {
        items.push(galleryItem, pricingItem, settingsItem); // Free users get Gallery, Pricing, and Settings
      } else {
        items.push(pricingItem, myPhotosItem, settingsItem); // Paid users get Photos, Pricing, and Settings
      }
    }

    // If admin, add Admin Console
    if (isAdmin) {
      items.push(adminItem);
    }

    return items;
  }, [t, isUserVerified, isAdmin, subscriptionResult?.plan]);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        // <-- changed from 50 to 200 for delayed sticky
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isWhiteInitially = WHITE_NAVBAR_PATHS.includes(location.pathname);
  const isWhite = scrolled || isWhiteInitially;
  const renderAuthButtons = () =>
    isUserVerified ? (
      isMobile ? (
        <Button
          variant="outlined"
          onClick={logout}
          sx={{
            width: "100%",
            textTransform: "none",
            color: customColors.gold,
            borderColor: customColors.gold,
            borderRadius: "25px",
            mb: 1,
          }}
        >
          {t("NAV.LOGOUT")}
        </Button>
      ) : (
        <Tooltip title="Log out">
          <LogoutButton onClick={logout} isBackgroundLight={isWhite} />
        </Tooltip>
      )
    ) : (
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "10px",
        }}
      >
        <FlagSelector />

        <CustomButton
          mode="secondary"
          text={t("NAV.LOGIN")}
          onClick={() => navigate("/login")}
          sx={{ padding: "5px 20px", fontSize: "14px" }}
        />

        <CustomButton
          text={t("NAV.SIGNUP")}
          onClick={() => navigate("/signup")}
          sx={{ padding: "5px 20px", fontSize: "14px" }}
        />
      </Box>
    );

  const MenuItem = ({ item }: { item: { text: string; link: string } }) => (
    <Button
      component={Link}
      to={item.link}
      color="inherit"
      sx={{
        color: isWhite ? "#000000" : "#ffffff",
        fontSize: "16px",
        textTransform: "none",
        "&:hover": { color: customColors.gold },
      }}
      onClick={() => {
        if (item.text === t("NAV.HOME") && location.pathname === "/") {
          window.location.reload();
        }
        trackNavigationClickEvent({
          navItem: item.text,
          url: item.link,
        });
      }}
    >
      {item.text}
    </Button>
  );

  const MobileDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
    >
      <Box
        sx={{
          width: 250,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          backgroundColor: customColors.background,
          p: 2,
        }}
        role="presentation"
        onClick={() => setDrawerOpen(false)}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              color: customColors.gold,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Virtual Staging&nbsp;
            <span style={{ color: "#B3B3B3" }}>Ai</span>
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography
            sx={{
              fontSize: "11px",
              color: customColors.silver,
              textAlign: "center",
              mb: "20px",
            }}
          >
            {isUserVerified ? user?.email : ""}
          </Typography>

          <List>
            {menuItems.map((item, index) => (
              <ListItemButton component={Link} to={item.link} key={index}>
                <ListItemText primary={item.text} sx={{ fontWeight: "bold" }} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Divider sx={{ my: 2 }} />
          {renderAuthButtons()}
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      {scrolled && <div style={{ height: "70px" }} />}
      <AppBar
        position={scrolled ? "fixed" : "static"}
        elevation={isWhite ? 4 : 0}
        sx={{
          background: isWhite ? "#ffffff" : blueGradient,
          color: isWhite ? "#000000" : "#ffffff",
          transition: "all 0.3s ease",
          padding: "10px 30px",
          boxShadow: isWhite ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
          zIndex: 1300,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "1355px",
            padding: "10px 0px !important",
            minHeight: "0 !important",
            margin: "0 auto",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              color: isWhite ? "#000000" : customColors.headerText,
              cursor: "pointer",
              margin: 0,
              padding: 0,
            }}
            onClick={() => {
              navigate("/");
            }}
          >
            <span style={{ fontWeight: "bold" }}>Virtual Staging</span> &nbsp;
            <span style={{ fontWeight: "bold", color: customColors.gold }}>
              PRO
            </span>{" "}
            &nbsp;
            <span
              style={{
                fontWeight: "lighter",
                color: isWhite ? "#000000" : "#B3B3B3",
              }}
            >
              Ai
            </span>
          </Typography>

          {!isMobile && (
            <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
              {menuItems.map((item, index) => (
                <MenuItem item={item} key={index} />
              ))}
            </Box>
          )}

          {isMobile ? (
            <>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon sx={{ color: customColors.gold }} />
              </IconButton>
              <MobileDrawer />
            </>
          ) : (
            <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {renderAuthButtons()}
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
};
